import { Constants } from '@pwc/pwc-deals-web-common';
import { createSelector, createSlice } from '@reduxjs/toolkit';
import { getHypothesisVersions } from './hypothesesService';
import { getKfa, getKfaSharedPrevious, getKfaVersions } from './kfaService';

export const initialState = {
  content: null,
  errorStatusCode: null,
  // undefined | previousVersion
  errorType: undefined,
  hasError: false,
  id: null,
  isLoading: true,
  kfa: null,
  name: null,
  selectedVersion: null,
  versions: null,
};

const hypothesisSlice = createSlice({
  name: 'hypothesis',
  initialState,
  reducers: {
    resetHypothesis: () => initialState,
    fetchHypothesisBegan: (state) => ({
      ...state,
      content: null,
      isLoading: true,
      hasError: false,
      errorType: undefined,
      errorStatusCode: null,
    }),
    fetchHypothesisSucceeded: (state, action) => ({
      ...state,
      ...action?.payload,
      isLoading: false,
      hasError: false,
      errorType: undefined,
      errorStatusCode: null,
    }),
    fetchKfaFailed: (state, { payload }) => ({
      ...state,
      isLoading: false,
      hasError: true,
      ...payload,
    }),
    setHypothesisContent: (state, action) => {
      const content = action.payload;
      state.content = content;
    },
    setHypothesisBody: (state, action) => ({
      ...state,
      body: action.payload,
    }),
    updateVersions: (state, { payload }) => {
      state.versions = [...(payload || [])];
    },
    setKfa: (state, action) => {
      if (!action.payload || !action.payload.content) {
        return {
          ...state,
          isLoading: false,
          kfa: undefined,
        };
      }

      return {
        ...state,
        isLoading: false,
        kfa: {
          ...action.payload,
          content: {
            ...action.payload.content,
            insightWithItems: action.payload.content.insight,
            insight:
              (action.payload.content.insight &&
                action.payload.content.insight.description) ||
              '',
          },
        },
      };
    },
    updateSelectionVersion: (state, action) => {
      state.selectedVersion = action.payload;
    },
  },
});
export default hypothesisSlice.reducer;

// Actions
export const {
  resetHypothesis,
  fetchHypothesisBegan,
  fetchHypothesisSucceeded,
  fetchKfaFailed,
  setHypothesisBody,
  setHypothesisContent,
  updateVersions,
  setKfa,
  updateSelectionVersion,
} = hypothesisSlice.actions;

// Selectors
export const getHypotheisId = (state) => state.hypothesis.id;
export const getHypothesisTitle = (state) => state.hypothesis.title;
export const getHypothesisLoading = (state) => state.hypothesis.isLoading;
export const getHypothesisError = (state) => state.hypothesis.hasError;
export const getHypothesisErrorState = ({
  hypothesis: { errorStatusCode, errorType, hasError },
}) => ({
  errorStatusCode,
  errorType,
  hasError,
});
export const getCurrentHypothesis = (state) => state.hypothesis;
export const getCurrentKfa = (state) => state.hypothesis.kfa;
export const kfaContentId = (state) => {
  if (state.hypothesis.selectedVersion?.contentId) {
    return state.hypothesis.selectedVersion.contentId;
  }
  return null;
};
export const viewingStatusCommentingPanel = (state) => {
  if (state.commentsMFE?.viewingStatus) {
    return state.commentsMFE?.viewingStatus;
  }
  return null;
};

export const selectHypothesisContent = createSelector(
  getCurrentHypothesis,
  (slice) => slice.content
);

export const selectHypothesisVersions = createSelector(
  getCurrentHypothesis,
  (slice) => slice.versions
);

export const selectedVersionStatus = createSelector(
  getCurrentHypothesis,
  ({ selectedVersion }) => selectedVersion
);

export const selectDraftVersion = createSelector(
  selectHypothesisVersions,
  (versions) => {
    return versions?.find(
      (item) =>
        item.versionStatus !== Constants.VERSION_STATUS.SHARED.value &&
        item.versionStatus !==
          Constants.VERSION_STATUS.SHARED_WITH_THIRD_PARTY.value
    );
  }
);

export const selectClientVersion = createSelector(
  selectHypothesisVersions,
  (versions) => {
    return versions?.find(
      (item) =>
        item.versionStatus === Constants.VERSION_STATUS.SHARED.value ||
        item.versionStatus ===
          Constants.VERSION_STATUS.SHARED_WITH_THIRD_PARTY.value
    );
  }
);
export const selectKfa = createSelector(getCurrentKfa, (slice) => slice);

export const selectHypothesisIfAvailable = (state) => {
  if (!state.hypothesis?.kfa) {
    return null;
  }

  return {
    richTextResolution: state.hypothesis.kfa.richTextResolution,
    isShared: !!state.hypothesis.kfa.content.shareWithAll,
  };
};

// Thunks

export const loadHypothesisVersions =
  (
    projectId,
    hypothesisContentThreadId
    // eslint-disable-next-line no-unused-vars
  ) =>
  async (dispatch) => {
    try {
      const versionsResponse = await getHypothesisVersions(
        projectId,
        hypothesisContentThreadId
      );
      dispatch(updateVersions(versionsResponse));
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log(error);
    }
  };

export const loadKfa =
  (projectId, keyFocusAreaId, shared = false, isMountedRef) =>
  async (dispatch) => {
    let action;

    try {
      const response = await getKfa(projectId, keyFocusAreaId, shared);
      action = setKfa(response.data);
    } catch (error) {
      action = fetchKfaFailed({
        errorStatusCode: error.response ? error.response.status : 500,
      });
    }

    if (isMountedRef?.current) {
      dispatch(action);
    }
  };

export const loadKfaVersions =
  (
    projectId,
    hypothesisContentThreadId,
    shared = false,
    isMountedRef
    // eslint-disable-next-line no-unused-vars
  ) =>
  async (dispatch) => {
    let action;

    try {
      const response = await getKfaVersions(
        projectId,
        hypothesisContentThreadId,
        shared
      );
      action = updateVersions(response);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log(error);

      action = fetchKfaFailed({
        errorStatusCode: error.response ? error.response.status : 500,
      });
    }

    if (isMountedRef?.current) {
      dispatch(action);
    }
  };

export const loadKfaSharedPrevious =
  ({ contentID, projectID, isMountedRef }) =>
  async (dispatch) => {
    const doDispatch = (action) => {
      if (isMountedRef?.current) {
        dispatch(action);
      }
    };

    try {
      doDispatch(
        setKfa((await getKfaSharedPrevious({ contentID, projectID })).data)
      );
      doDispatch(hypothesisSlice.actions.fetchHypothesisSucceeded());
    } catch (err) {
      doDispatch(
        fetchKfaFailed({
          errorStatusCode: err.response ? err.response.status : 500,
          errorType: 'previousVersion',
        })
      );
    }
  };
